'use client'

import { useEffect, useState } from 'react'
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'framer-motion'

/* ── Types ────────────────────────────────────────────── */
export interface Segment {
  label: string
  amount: number
  color: string
}

interface Props {
  segments: Segment[]
  /** Optional label shown beneath the total, e.g. "/month" */
  totalSuffix?: string
  /** Light backgrounds need dark text; default is dark (white text) */
  variant?: 'dark' | 'light'
  /** Max total to size arcs against — pass the same value across
   *  two side-by-side donuts so they share a scale. If omitted the
   *  component uses the sum of its own segments. */
  maxTotal?: number
  className?: string
}

/* ── Geometry ──────────────────────────────────────────── */
const RADIUS = 100
const STROKE = 26
const PAD = 90
const CX = RADIUS + STROKE / 2 + PAD
const CY = RADIUS + STROKE / 2 + PAD
const VB = (RADIUS + STROKE / 2) * 2 + PAD * 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const LABEL_LINE_LEN = 26
const LABEL_TICK = 14

/* ── Animated counter ─────────────────────────────────── */
function AnimatedTotal({ value, className }: { value: number; className?: string }) {
  const motionVal = useMotionValue(0)
  const display = useTransform(motionVal, (v) => `$${Math.round(v).toLocaleString()}`)
  const [text, setText] = useState('$0')

  useEffect(() => {
    const unsub = display.on('change', (v) => setText(v))
    return unsub
  }, [display])

  useEffect(() => {
    const ctrl = animate(motionVal, value, { duration: 0.8, ease: [0.16, 1, 0.3, 1] })
    return () => ctrl.stop()
  }, [value, motionVal])

  return <span className={className}>{text}</span>
}

/* ── Component ────────────────────────────────────────── */
export default function CompensationDonut({
  segments,
  totalSuffix = '/month',
  variant = 'light',
  maxTotal,
  className = '',
}: Props) {
  const total = segments.reduce((s, seg) => s + seg.amount, 0)
  const effectiveMax = maxTotal ?? total

  const isDark = variant === 'dark'
  const trackColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'
  const labelFill = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.7)'
  const lineFill = isDark ? 0.5 : 0.35
  const totalColor = isDark ? '#f8fafc' : 'var(--text-1)'
  const suffixColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-3)'

  const arcs = segments
    .filter((seg) => seg.amount > 0)
    .map((seg, i, arr) => {
      const fraction = effectiveMax > 0 ? seg.amount / effectiveMax : 0
      const dashLen = fraction * CIRCUMFERENCE
      const dashGap = CIRCUMFERENCE - dashLen
      const preceding = arr.slice(0, i).reduce((s, p) => s + p.amount, 0)
      const offset = effectiveMax > 0 ? -(preceding / effectiveMax) * CIRCUMFERENCE : 0

      const midFraction = effectiveMax > 0 ? (preceding + seg.amount / 2) / effectiveMax : 0
      const angle = midFraction * 2 * Math.PI - Math.PI / 2
      const outerR = RADIUS + STROKE / 2
      const lineStartX = CX + Math.cos(angle) * outerR
      const lineStartY = CY + Math.sin(angle) * outerR
      const lineEndX = CX + Math.cos(angle) * (outerR + LABEL_LINE_LEN)
      const lineEndY = CY + Math.sin(angle) * (outerR + LABEL_LINE_LEN)
      const isRight = lineEndX >= CX
      const tickEndX = lineEndX + (isRight ? LABEL_TICK : -LABEL_TICK)
      const textAnchor = isRight ? ('start' as const) : ('end' as const)

      return {
        ...seg,
        dashLen,
        dashGap,
        offset,
        lineStartX,
        lineStartY,
        lineEndX,
        lineEndY,
        tickEndX,
        textAnchor,
      }
    })

  return (
    <div className={`relative ${className}`}>
      <div className="relative mx-auto w-full" style={{ maxWidth: 380, aspectRatio: '1 / 1' }}>
        {/* donut arcs (rotated -90 so 0° = top) */}
        <svg viewBox={`0 0 ${VB} ${VB}`} className="absolute inset-0 h-full w-full -rotate-90" aria-hidden="true">
          <circle cx={CX} cy={CY} r={RADIUS} fill="none" stroke={trackColor} strokeWidth={STROKE} />
          {arcs.map((arc) => (
            <motion.circle
              key={arc.label}
              cx={CX}
              cy={CY}
              r={RADIUS}
              fill="none"
              stroke={arc.color}
              strokeWidth={STROKE}
              strokeLinecap="butt"
              initial={{ strokeDasharray: `0 ${CIRCUMFERENCE}`, strokeDashoffset: 0 }}
              animate={{
                strokeDasharray: `${arc.dashLen} ${arc.dashGap}`,
                strokeDashoffset: arc.offset,
              }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            />
          ))}
        </svg>

        {/* callout lines + labels (not rotated) */}
        <svg viewBox={`0 0 ${VB} ${VB}`} className="absolute inset-0 h-full w-full" aria-hidden="true">
          <AnimatePresence>
            {arcs.map((arc) => (
              <motion.g
                key={arc.label}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <line
                  x1={arc.lineStartX} y1={arc.lineStartY}
                  x2={arc.lineEndX} y2={arc.lineEndY}
                  stroke={arc.color} strokeWidth={1.5} opacity={lineFill}
                />
                <line
                  x1={arc.lineEndX} y1={arc.lineEndY}
                  x2={arc.tickEndX} y2={arc.lineEndY}
                  stroke={arc.color} strokeWidth={1.5} opacity={lineFill}
                />
                <text
                  x={arc.tickEndX + (arc.textAnchor === 'start' ? 5 : -5)}
                  y={arc.lineEndY - 6}
                  textAnchor={arc.textAnchor}
                  dominantBaseline="middle"
                  fill={labelFill}
                  fontSize={11}
                  fontWeight={600}
                  fontFamily="system-ui, sans-serif"
                >
                  {arc.label}
                </text>
                <text
                  x={arc.tickEndX + (arc.textAnchor === 'start' ? 5 : -5)}
                  y={arc.lineEndY + 8}
                  textAnchor={arc.textAnchor}
                  dominantBaseline="middle"
                  fill={labelFill}
                  fontSize={10}
                  fontWeight={400}
                  fontFamily="system-ui, sans-serif"
                  opacity={0.6}
                >
                  ${arc.amount.toLocaleString()}
                </text>
              </motion.g>
            ))}
          </AnimatePresence>
        </svg>

        {/* center total */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <AnimatedTotal
            value={total}
            className="text-3xl font-bold tabular-nums leading-none sm:text-4xl"
          />
          <span className="mt-1 text-xs font-medium" style={{ color: suffixColor }}>
            {totalSuffix}
          </span>
        </div>
      </div>
    </div>
  )
}
